import { JSX, UIEvent, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDOM from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { useFocusTrap } from 'hooks/useFocusTrap';
import { useEscapeKey } from 'hooks/useEscapeKey';
import { useViewportWidth } from 'hooks/useViewportWidth';
import { useTranslation } from 'react-i18next';

interface SlideUpModalProps {
   isOpen: boolean;
   setIsOpen: (isOpen: boolean) => void;
   children: JSX.Element | string | undefined;
   className?: string | undefined;
   onScroll?: (event: UIEvent<HTMLDivElement>) => void;
   drag?: boolean | undefined;
   ariaLabel?: string;
   titleId?: string;
   autoFocus?: boolean;
   /**
    * NPT-1800 — render as a centred dialog from 768px up instead of a
    * full-width bottom sheet. Set by callers that show the mobile filter on a
    * desktop viewport (the org landing hero's one-third search column), where a
    * sheet spanning a 1920px screen to pick a date reads as a bug. Off by
    * default, so every existing caller keeps the sheet.
    */
   desktopCentered?: boolean;
   /**
    * NPT-1968 — how background scroll is locked while open. `fixed-body`
    * (default) pins <body> with position:fixed. On iOS Safari, pinning the
    * body while the keyboard opens, closes, or is still animating away leaves
    * every fixed layer under it (this sheet, its backdrop, and later sheets)
    * painted only down to where the keyboard's top edge was, with page
    * background below. So pass `overflow` for sheets with a text input and
    * for sheets that open straight after one (the mobile search flow chains
    * location → dates → rooms). It keeps the body in flow, so the sheet isn't
    * a fixed layer nested in another one.
    */
   scrollLock?: 'fixed-body' | 'overflow';
}

const SlideUpModal = (props: SlideUpModalProps) => {
   const { isOpen, setIsOpen, children, className = '', drag = true, onScroll, ariaLabel, titleId, autoFocus = true, desktopCentered = false, scrollLock = 'fixed-body' } = props;
   const { t } = useTranslation();
   const modalRef = useRef<HTMLDivElement>(null);
   const [pointStart, setPointStart]: any = useState(null);
   const [move, setMove]: any = useState(null);
   const [isTop, setIsTop]: any = useState(true);
   const viewportWidth = useViewportWidth();

   // Resolved in JS rather than with `md:` classes because it also swaps the
   // entrance animation, and because framer-motion writes an inline `transform`
   // that a `md:-translate-*` centring class could not survive.
   const isCentered = desktopCentered && viewportWidth >= 768;
   // Drag-to-dismiss is a sheet gesture; a centred dialog closes via Esc,
   // the backdrop, or its own close button.
   const canDrag = drag && !isCentered;

   useFocusTrap(isOpen, modalRef, autoFocus);
   useEscapeKey(isOpen, () => setIsOpen(false), modalRef);

   const onTouchStart = (event: any) => {
      if (isTop && canDrag) {
         const { pageY } = event.touches[0];
         setPointStart(pageY);
      }
   };
   const onTouchEnd = () => {
      if (move && move > 60 && canDrag) {
         setIsOpen(false);
      }
      setPointStart(null);
      setMove(null);
   };
   const onTouchMove = (event: any) => {
      if (!canDrag) return;
      const { pageY } = event.touches[0];
      if (isTop && pointStart == null) {
         setPointStart(pageY);
      } else if (pointStart !== null) {
         setMove(pageY - pointStart);
      }
   };

   useEffect(() => {
      if (!isOpen || scrollLock !== 'overflow') return;

      // Safari honours overflow:hidden on the root for touch scrolling from
      // iOS 16; overscroll-behavior stops the rubber band from dragging the
      // page behind the sheet.
      const scrollY = window.scrollY;
      const root = document.documentElement.style;
      root.setProperty('overflow', 'hidden');
      root.setProperty('overscroll-behavior', 'none');

      return () => {
         root.removeProperty('overflow');
         root.removeProperty('overscroll-behavior');
         // The keyboard may have scrolled the window to reveal the input.
         window.scrollTo(0, scrollY);
      };
   }, [isOpen, scrollLock]);

   useEffect(() => {
      if (!isOpen || scrollLock !== 'fixed-body') return;

      // `overflow: hidden` alone doesn't block touch-drag scrolling of the
      // background on iOS Safari — it still lets the page scroll under the
      // modal, and Safari's position:fixed handling then makes the modal
      // appear to jump/reposition when that happens. Pinning the body with
      // position:fixed (restoring the exact scroll offset on close) is the
      // reliable cross-browser way to lock background scroll.
      const scrollY = window.scrollY;
      document.documentElement.style.setProperty('overflow', 'hidden');
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
         document.documentElement.style.removeProperty('overflow');
         document.body.style.position = '';
         document.body.style.top = '';
         document.body.style.width = '';
         window.scrollTo(0, scrollY);
      };
   }, [isOpen, scrollLock]);

   return ReactDOM.createPortal(
      <AnimatePresence>
         {isOpen && (
            <>
               <motion.div
                  // Without a pinned body, a drag that starts on the backdrop would pan the page.
                  className={twMerge('fixed inset-0 bg-black/50 z-40', scrollLock === 'overflow' && 'touch-none')}
                  aria-hidden="true"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsOpen(false)}
               />

               <motion.div
                  ref={modalRef}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby={titleId}
                  aria-label={titleId ? undefined : ariaLabel || t('a11y.dialog.generic')}
                  tabIndex={-1}
                  className={twMerge(
                     'fixed bottom-0 left-0 w-screen bg-white rounded-t-2xl shadow-lg z-50 focus:outline-none',
                     className,
                     // Appended last so it outranks a caller's sheet-sized
                     // `m-*`/`w-*` in twMerge. `inset-0 + m-auto + h-fit`
                     // centres a fixed element without a transform.
                     isCentered && 'inset-0 m-auto h-fit w-[min(560px,92vw)] max-h-[85vh] overflow-y-auto rounded-2xl',
                  )}
                  initial={isCentered ? { opacity: 0, scale: 0.96 } : { y: '100%' }}
                  animate={isCentered ? { opacity: 1, scale: 1 } : { y: 0 }}
                  exit={isCentered ? { opacity: 0, scale: 0.96 } : { y: '100%' }}
                  transition={isCentered ? { duration: 0.18, ease: 'easeOut' } : { type: 'spring', stiffness: 200, damping: 25 }}
                  onScroll={(event: any) => {
                     onScroll?.(event);
                     setIsTop(!event?.target?.scrollTop);
                  }}
                  onTouchStart={onTouchStart}
                  onTouchEnd={onTouchEnd}
                  onTouchMove={onTouchMove}
               >
                  {children}
               </motion.div>
            </>
         )}
      </AnimatePresence>,
      document.body,
   );
};

export default SlideUpModal;
